import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, Heart, Instagram, Facebook, Youtube, ArrowRight } from 'lucide-react'

const BRANCHES = [
  { name: 'BRC Karur', city: 'Karur', slug: 'karur' },
  { name: 'BRC Velur', city: 'Namakkal', slug: 'velur' },
  { name: 'BRC Salem', city: 'Salem', slug: 'salem' },
  { name: 'BRC Coimbatore', city: 'Coimbatore', slug: 'coimbatore' },
  { name: 'BRC Trichy', city: 'Trichy', slug: 'trichy' },
  { name: 'Crystal Tirupur', city: 'Tirupur', slug: 'tirupur' },
  { name: 'Crystal Avinashi', city: 'Avinashi', slug: 'avinashi' },
  { name: 'BRC Erode', city: 'Erode', slug: 'erode' },
]

const SERVICES = [
  { label: 'Occupational Therapy', href: '/services/occupational-therapy' },
  { label: 'Speech & Language Therapy', href: '/services/speech-language-therapy' },
  { label: 'Physiotherapy', href: '/services/physiotherapy' },
  { label: 'ABA Therapy', href: '/services/aba-therapy' },
  { label: 'Sensory Integration', href: '/services/sensory-integration' },
  { label: 'Hearing Aid & Audiology', href: '/services/hearing-aid' },
]

const QUICK_LINKS = [
  { label: 'About BRC', href: '/about' },
  { label: 'Our Team', href: '/team' },
  { label: 'Branches', href: '/branches' },
  { label: 'Conditions We Treat', href: '/conditions' },
  { label: 'Blog & Resources', href: '/blog' },
  { label: 'For Physicians', href: '/for-physicians' },
  { label: 'Insurance', href: '/insurance' },
  { label: 'Contact Us', href: '/contact' },
]

export function Footer() {
  return (
    <footer className="bg-primary-900 text-white" role="contentinfo">
      {/* CTA strip */}
      <div className="bg-gradient-primary border-b border-white/10">
        <div className="container mx-auto py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white/70 text-sm font-medium uppercase tracking-widest mb-1">
              Start Your Child's Journey
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white">
              Book a Free Assessment Today
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              to="/book"
              className="btn bg-accent text-white hover:bg-accent-600 btn-lg shadow-button"
            >
              Book Appointment <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="https://wa.me/919655488554"
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-white/15 text-white hover:bg-white/25 border border-white/30 btn-lg"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="container mx-auto py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-white rounded-xl px-3 py-2 inline-flex items-center shadow-sm">
                <img
                  src="/images/brc-logo.webp"
                  alt="Brainchild Rehab Centre"
                  className="h-10 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Evidence-based pediatric rehabilitation across 8 centres in Tamil Nadu. Transforming
              disability into ability, one child at a time.
            </p>
            <div className="space-y-3">
              <a
                href="tel:+919655488554"
                className="flex items-center gap-3 text-white/70 hover:text-white text-sm transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-accent/30 transition-colors">
                  <Phone className="w-3.5 h-3.5" />
                </div>
                +91 96554 88554
              </a>
              <a
                href="mailto:info@brainchildrehabcentre.com"
                className="flex items-center gap-3 text-white/70 hover:text-white text-sm transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-accent/30 transition-colors">
                  <Mail className="w-3.5 h-3.5" />
                </div>
                info@brainchildrehabcentre.com
              </a>
            </div>
            {/* Social */}
            <div className="flex gap-2 mt-6">
              {[
                { icon: Instagram, href: 'https://www.instagram.com/brc_rehab_hearingaid_centre/', label: 'Instagram' },
                { icon: Facebook, href: 'https://www.facebook.com/brainchildrehabcentre', label: 'Facebook' },
                { icon: Youtube, href: 'https://www.youtube.com/@brainchildrehabcentre', label: 'YouTube' },
              ].map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-accent/40 transition-colors text-white/70 hover:text-white"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-5">
              Our Services
            </h3>
            <ul className="space-y-2.5">
              {SERVICES.map((s) => (
                <li key={s.href}>
                  <Link
                    to={s.href}
                    className="text-white/60 hover:text-accent-300 text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                    {s.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" className="text-accent-300 text-sm font-semibold hover:text-accent-300 transition-colors">
                  View All 14 Services →
                </Link>
              </li>
            </ul>
          </div>

          {/* Branches */}
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-5">
              Our Branches
            </h3>
            <ul className="space-y-2.5">
              {BRANCHES.map((b) => (
                <li key={b.slug}>
                  <Link
                    to={`/branches/${b.slug}`}
                    className="text-white/60 hover:text-accent-300 text-sm transition-colors flex items-center gap-2 group"
                  >
                    <MapPin className="w-3 h-3 flex-shrink-0 text-white/55 group-hover:text-accent-300 transition-colors" />
                    <span>{b.name}</span>
                    <span className="text-white/60 text-xs">{b.city}</span>
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/branches" className="text-accent-300 text-sm font-semibold hover:text-accent-300 transition-colors">
                  Find Nearest Branch →
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-5">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-white/60 hover:text-accent-300 text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Certifications */}
            <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
              <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">
                Recognised by
              </p>
              <div className="flex flex-wrap gap-2">
                {['RCI', 'BASLP', 'IAP', 'DISHA'].map((cert) => (
                  <span key={cert} className="px-2.5 py-1 bg-white/10 rounded-lg text-white/70 text-xs font-bold">
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/65 text-xs text-center sm:text-left">
            © {new Date().getFullYear()} Brainchild Rehab Centre. All rights reserved. Built by{' '}
            <a
              href="https://leadup.in"
              target="_blank"
              rel="noopener"
              title="Leadup Technologies — web design &amp; development"
              aria-label="Leadup Technologies (opens in a new tab)"
              className="font-semibold text-white/90 underline underline-offset-2 hover:text-accent-300 transition-colors"
            >
              Leadup Technologies
            </a>.
          </p>
          <p className="text-white/55 text-xs flex items-center gap-1">
            Made with <Heart className="w-3 h-3 text-red-400 fill-red-400" /> for children across Tamil Nadu
          </p>
        </div>
      </div>
    </footer>
  )
}
